import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  DISMISS_TYPE,
  FOCUS_TYPE,
  type ShowoffItemFormatted,
  type StepType,
  type FocusType,
  type DismissType,
} from '../types';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { setShowoff } from '@/store/actions';

type UseCurrentSelectedParams = {
  toShow: ShowoffItemFormatted | null;
  setToShow: Dispatch<SetStateAction<ShowoffItemFormatted | null>>;
};

type SelectedConfig = {
  attr: string;
  focusType: FocusType;
  bypassCondition?: (elem: HTMLElement) => boolean;
  dismissType?: DismissType;
};

const resolveElement = (attr: string | null) =>
  attr ? document.querySelector<HTMLElement>(`[data-showoff="${attr}"]`) : null;

export const useCurrentSelected = ({
  toShow,
  setToShow,
}: UseCurrentSelectedParams) => {
  const [waitForDismiss, setWaitForDismiss] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<SelectedConfig | null>(
    null,
  );
  const [inView, setInView] = useState(false);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const showoffContext = useStore(useShallow((s) => s.showoffContext));
  const gameWindowOpen = useStore((s) => s.gameWindowContext.gameWindowOpen);

  const viewedFeatureRef = useRef<string | null | undefined>(toShow?.name);
  if (toShow?.name !== viewedFeatureRef.current) {
    viewedFeatureRef.current = toShow?.name;
    setHasBeenViewed(false);
  }

  const currentSelected = useMemo(() => {
    if (!selectedConfig) return null;
    const element = resolveElement(selectedConfig.attr);
    if (!element) return null;
    return {
      element,
      focusType: selectedConfig.focusType,
      bypassCondition: selectedConfig.bypassCondition,
      dismissType: selectedConfig.dismissType,
    };
  }, [selectedConfig]);

  const checkBypassOnNext = useCallback((step: StepType): boolean => {
    const elem = resolveElement(step.attr);
    return !!(step.bypassCondition && elem && step.bypassCondition(elem));
  }, []);

  const applyNotificationsAndBadges = useCallback(
    (revoke = false) => {
      const notificationType = {
        BADGE: 'data-showoff-badge',
        NOTIFICATION: 'data-showoff-notification',
      } as const;

      const steps = revoke ? showoffContext?.feature?.steps : toShow?.steps;

      steps?.forEach((step, index) => {
        const element = resolveElement(step.attr);
        const type = element
          ?.getAttributeNames()
          .find((attr) =>
            Object.values(notificationType).includes(
              attr as (typeof notificationType)[keyof typeof notificationType],
            ),
          );

        if (
          type &&
          (!(index === 0 && step.focusType !== FOCUS_TYPE.NOTHING) ||
            toShow?.notice)
        ) {
          element?.setAttribute(type, revoke ? 'false' : 'true');
        }
      });
    },
    [toShow, showoffContext?.feature?.steps],
  );

  const invokeNextStep = useCallback(() => {
    setToShow((prev) => {
      if (prev?.steps && prev.steps.length > 0) {
        const [, ...remainSteps] = prev.steps;
        const remain = remainSteps.filter((item) => !checkBypassOnNext(item));
        return { ...prev, steps: remain };
      }
      return null;
    });
  }, [setToShow, checkBypassOnNext]);

  const popupAction = useCallback(() => {
    setTimeout(() => {
      invokeNextStep();
    }, 200);
  }, [invokeNextStep]);

  useEffect(() => {
    if (!toShow?.name) {
      observerRef.current?.disconnect();
      return;
    }

    const element = resolveElement(toShow.name);
    if (!element) return;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(([entry]) => {
      const isVisible = entry.isIntersecting;
      setInView(isVisible);

      if (isVisible) {
        setHasBeenViewed(true);
      }

      if (toShow.steps.length > 0 && toShow.notice && isVisible) {
        setWaitForDismiss(true);
      }
    });
    observerRef.current.observe(element);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [toShow, gameWindowOpen]);

  const prevAttrRef = useRef<string | null>(null);

  useEffect(() => {
    if (toShow && toShow.steps.length > 0 && toShow.show) {
      const prevElement = resolveElement(prevAttrRef.current);
      if (prevElement) {
        prevElement.style.zIndex = '2';
      }

      const current = toShow.steps[0];
      const element = resolveElement(current.attr);
      prevAttrRef.current = current.attr;

      if (!element) {
        requestAnimationFrame(() => setSelectedConfig(null));
        return;
      }

      requestAnimationFrame(() =>
        setSelectedConfig({
          attr: current.attr,
          focusType: current.focusType,
          bypassCondition: current.bypassCondition,
          dismissType: current.dismissType,
        }),
      );

      setShowoff({
        currentStep: {
          element,
          focusType: current.focusType,
          bypassCondition: current.bypassCondition,
          dismissType: current.dismissType,
          name: current.attr,
        },
      });
    } else if (!waitForDismiss) {
      requestAnimationFrame(() => setSelectedConfig(null));
    }
  }, [toShow, waitForDismiss]);

  useEffect(() => {
    if (!selectedConfig) return;

    const element = resolveElement(selectedConfig.attr);
    if (!element) return;

    element.style.zIndex = '5';

    if (selectedConfig.dismissType === DISMISS_TYPE.ACTION) {
      element.addEventListener('click', popupAction);
    } else {
      const targetElement = toShow?.name ? resolveElement(toShow.name) : null;

      if (targetElement) {
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 500);
      }
      requestAnimationFrame(() => setWaitForDismiss(true));
    }

    return () => {
      element.removeEventListener('click', popupAction);
    };
  }, [selectedConfig, popupAction, toShow]);

  const setCurrentSelected = useCallback((value: null) => {
    if (value === null) {
      setSelectedConfig(null);
    }
  }, []);

  return {
    waitForDismiss,
    setWaitForDismiss,
    applyNotificationsAndBadges,
    currentSelected,
    setCurrentSelected,
    checkBypassOnNext,
    inView,
    hasBeenViewed,
  };
};
